import { Button, Chip, ChipProps, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { PlayIcon } from 'lucide-react';
import { JobStatus } from '../../types/enums/JobStatus.enum';
import { Job } from '../../types/Job/Job.type';
import { isJobActive } from '../../utils/jobStatus';

interface JobRowProps {
  job: Job;
  isLoading: boolean;
  onRun: (typeId: number) => void;
}

const statusColors: Record<number, ChipProps['color']> = {
  [JobStatus.QUEUED]: 'default',
  [JobStatus.IN_PROGRESS]: 'info',
  [JobStatus.DONE]: 'success',
  [JobStatus.FAILED]: 'error',
};

const formatDate = (date: string | null) => (date ? format(parseISO(date), 'MMM d, yyyy HH:mm') : '-');

const triggeredBy = (job: Job) => {
  if (!job.status) return '-';
  if (!job.user) return 'Scheduled';
  const fullName = `${job.user.first_name ?? ''} ${job.user.last_name ?? ''}`.trim();
  return fullName || job.user.email;
};

function JobRow({ job, isLoading, onRun }: JobRowProps) {
  const isActive = isJobActive(job);

  return (
    <TableRow hover>
      <TableCell width={300}>
        <Typography variant='body2' sx={{ fontWeight: 500 }}>
          {job.type.name}
        </Typography>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {job.type.description || 'No description'}
        </Typography>
      </TableCell>
      <TableCell>
        {job.status ? (
          <Tooltip title={job.error_message ?? ''}>
            <Chip size='small' label={job.status.name} color={statusColors[job.status.id] ?? 'default'} />
          </Tooltip>
        ) : (
          <Chip size='small' label='Never run' variant='outlined' />
        )}
      </TableCell>
      <TableCell>
        <Typography variant='body2' component='span'>
          {triggeredBy(job)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>{formatDate(job.created_at)}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2'>{formatDate(job.updated_at)}</Typography>
      </TableCell>
      <TableCell align='right' width={120}>
        <Tooltip title={isActive ? 'Job is already running' : ''}>
          <span>
            <Button
              variant='contained'
              disableElevation
              startIcon={<PlayIcon size={16} />}
              disabled={isLoading || isActive}
              onClick={() => onRun(job.type.id)}
              aria-label={`Run ${job.type.name}`}
            >
              Run
            </Button>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default JobRow;
